/**
 * Utility functions for determining form field requirements.
 * Extracted for testability and reusability.
 */

export interface FieldRequirementContext {
  primaryCareProvider: boolean;
  isEditingSelf: boolean;
}

export interface FieldRequirementResult {
  required: boolean;
  clearError: boolean;
}

/**
 * Determines if a specific field should be required based on the user context.
 * Used in EditUser form to dynamically set field validation rules.
 *
 * @param fieldId - The field identifier (e.g., "mpcNumber", "signImageFileId", "letterHeaderLogo")
 * @param context - The context containing primaryCareProvider and isEditingSelf flags
 * @returns Object with `required` and `clearError` properties
 */
export function getFieldRequirement(fieldId: string, context: FieldRequirementContext): FieldRequirementResult {
  const { primaryCareProvider, isEditingSelf } = context;

  if (fieldId !== "mpcNumber" && fieldId !== "signImageFileId" && fieldId !== "letterHeaderLogo") {
    return { required: false, clearError: false };
  }

  if (!primaryCareProvider) {
    return { required: false, clearError: true };
  }

  switch (fieldId) {
    case "mpcNumber":
      // MPC number is always required for primary care providers
      return { required: true, clearError: false };
    case "signImageFileId":
      // Signature is required only when editing your own profile
      return { required: isEditingSelf, clearError: false };
    case "letterHeaderLogo":
      // Letter header logo is always optional
      return { required: false, clearError: false };

    default:
      return { required: false, clearError: false };
  }
}

/**
 * Updates form data array with dynamic field requirements.
 *
 * @param formData - Array of form field objects with id, required, and isError properties
 * @param context - The context containing primaryCareProvider and isEditingSelf flags
 * @returns Updated form data array with adjusted required and isError properties
 */
export function updateFormFieldRequirements<T extends { id: string; required: boolean; isError: boolean }>(
  formData: T[],
  context: FieldRequirementContext
): T[] {
  return formData.map(item => {
    const result = getFieldRequirement(item.id, context);

    if (item.id === "mpcNumber" || item.id === "signImageFileId" || item.id === "letterHeaderLogo") {
      return {
        ...item,
        required: result.required,
        isError: result.clearError ? false : item.isError,
      };
    }

    return item;
  });
}
